import {
  Radio as MantineRadio,
  RadioGroupProps as MantineRadioGroupProps,
} from '@mantine/core';
import { createContext, forwardRef, useId } from 'react';

import styles from './RadioGroup.module.css';

export const RadioGroupReadOnlyContext = createContext(false);

export type RadioGroupProps = Omit<
  MantineRadioGroupProps,
  'styles' | 'classNames' | 'style' | 'readOnly'
> & {
  /**
   * When true, the selection can't be changed and only the selected RadioCard
   * renders, in its read-only style. Regular Radio components stay visible.
   */
  isReadOnly?: boolean;
};

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ children, isReadOnly = false, name, ...props }, ref) => {
    // Radios mutually exclude by DOM `name`. Separate react-hook-form instances can
    // still each spread a `field` of the same name — employee and spouse both pass
    // name="gender" — and with no wrapping <form> those ownerless radios collapse
    // into one native group that silently unchecks the other section. Namespacing
    // per instance keeps the caller's name readable while guaranteeing uniqueness.
    const instanceId = useId();
    const groupName = `${name ?? 'radio-group'}-${instanceId}`;

    return (
      <MantineRadio.Group
        ref={ref}
        name={groupName}
        readOnly={isReadOnly}
        classNames={{
          label: styles.label,
          required: styles.required,
          error: styles.error,
          description: styles.description,
        }}
        {...props}
      >
        <RadioGroupReadOnlyContext.Provider value={isReadOnly}>
          {children}
        </RadioGroupReadOnlyContext.Provider>
      </MantineRadio.Group>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
